import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Droplets,
    PackageOpen,
    ListChecks,
    CreditCard,
    Settings,
    Users,
    Mail,
    LogOut,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const MAIN = [
    { to: "/app", label: "Resumen", icon: LayoutDashboard, end: true, testId: "nav-resumen" },
    { to: "/app/analisis", label: "Mi piel", icon: Droplets, testId: "nav-piel" },
    { to: "/app/neceser", label: "Mi neceser", icon: PackageOpen, testId: "nav-neceser" },
    { to: "/app/resultado", label: "Mi resultado", icon: ListChecks, testId: "nav-resultado" },
    { to: "/app/cuenta", label: "Mi cuenta", icon: CreditCard, testId: "nav-cuenta" },
];
const ADMIN = [
    { to: "/app/admin/usuarios", label: "Usuarios", icon: Users, testId: "nav-admin-usuarios" },
    { to: "/app/admin/correos", label: "Correos", icon: Mail, testId: "nav-admin-correos" },
];
const BOTTOM = MAIN.slice(0, 5);

function itemClass(isActive) {
    return `flex items-center gap-3 rounded-lg px-3 py-2 text-[15px] font-medium transition-colors duration-150 ${
        isActive ? "bg-terracotta/10 text-terracotta" : "text-subtle hover:bg-secondary hover:text-ink"
    }`;
}

export function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Sidebar escritorio */}
            <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-line bg-card lg:flex">
                <div className="px-6 py-6">
                    <Brand to="/app" />
                </div>
                <nav className="flex-1 space-y-1 px-3">
                    {MAIN.map((it) => (
                        <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
                            className={({ isActive }) => itemClass(isActive)}>
                            <it.icon size={18} strokeWidth={2} />
                            {it.label}
                        </NavLink>
                    ))}
                    {isAdmin && (
                        <>
                            <p className="px-3 pb-1 pt-5 text-[12px] font-medium uppercase tracking-wide text-subtle">
                                Administración
                            </p>
                            {ADMIN.map((it) => (
                                <NavLink key={it.to} to={it.to} data-testid={it.testId}
                                    className={({ isActive }) => itemClass(isActive)}>
                                    <it.icon size={18} strokeWidth={2} />
                                    {it.label}
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>
                <div className="border-t border-line p-3">
                    <NavLink to="/app/ajustes" data-testid="nav-ajustes"
                        className={({ isActive }) => itemClass(isActive)}>
                        <Settings size={18} /> Ajustes
                    </NavLink>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="user-menu-button"
                                className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-150 hover:bg-secondary"
                            >
                                <Avatar name={user?.name} size={36} seed={user?.email} />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[14px] font-medium text-ink">{user?.name}</p>
                                    <p className="truncate text-[12px] text-subtle">{user?.email}</p>
                                </div>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => navigate("/app/cuenta")}>Mi cuenta</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/app/ajustes")}>Ajustes</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={onLogout} data-testid="logout-button">
                                <LogOut size={15} className="mr-2" /> Cerrar sesión
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Barra superior móvil */}
            <div className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-card/90 px-4 py-3 backdrop-blur lg:hidden">
                <Brand to="/app" size="text-[18px]" />
                <button onClick={onLogout} data-testid="logout-button-mobile" className="text-[14px] text-subtle">
                    Salir
                </button>
            </div>

            <main className="px-4 pb-28 pt-6 lg:ml-64 lg:px-10 lg:pb-16 lg:pt-10">
                <div className="mx-auto max-w-5xl">
                    <Outlet />
                </div>
            </main>

            {/* Nav inferior móvil */}
            <nav className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-5 border-t border-line bg-card lg:hidden">
                {BOTTOM.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.end} data-testid={`${it.testId}-mobile`}
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors duration-150 ${
                                isActive ? "text-terracotta" : "text-subtle"
                            }`
                        }>
                        <it.icon size={20} />
                        {it.label}
                    </NavLink>
                ))}
            </nav>
        </div>
    );
}
